import { useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";

// Helper function to check if on mobile
const isMobile = () => window.innerWidth < 768;

// Animation variants with mobile optimizations
export const fadeInUp = {
  hidden: { 
    opacity: 0, 
    y: isMobile() ? 10 : 20 
  },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: isMobile() ? 0.4 : 0.6,
      ease: "easeOut"
    }
  }
};

export const fadeIn = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { 
      duration: isMobile() ? 0.3 : 0.5,
      ease: "easeOut"
    }
  }
};

export const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: isMobile() ? 0.08 : 0.1,
      delayChildren: isMobile() ? 0.1 : 0.2,
      ease: "easeOut"
    }
  }
};

export const slideIn = {
  hidden: { 
    x: isMobile() ? -10 : -20, 
    opacity: 0 
  },
  visible: { 
    x: 0, 
    opacity: 1,
    transition: { 
      duration: isMobile() ? 0.3 : 0.5,
      ease: "easeOut"
    }
  }
};

// Mobile-specific animations
export const mobileMenuSlide = {
  hidden: { x: '100%' },
  visible: { 
    x: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30
    }
  }
};

export const mobileMenuFade = {
  hidden: { opacity: 0, x: 20 },
  visible: (custom) => ({ 
    opacity: 1, 
    x: 0,
    transition: {
      delay: custom?.delay || 0.1,
      duration: 0.3
    }
  })
};

// Enhanced scroll animation hook with performance optimizations
export function useScrollInView(options = { once: true, amount: isMobile() ? 0.2 : 0.3 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    ...options,
    // Reduced threshold on mobile for earlier animation triggering
    amount: isMobile() ? Math.min(options.amount, 0.2) : options.amount
  });
  
  return [ref, isInView];
}

// Dynamic animations based on screen size
export function useDynamicAnimations() {
  const [animations, setAnimations] = useState({
    fadeInUp,
    fadeIn,
    staggerContainer,
    slideIn
  });

  useEffect(() => {
    const handleResize = () => {
      // Re-generate animations based on current screen size
      const mobile = window.innerWidth < 768;
      
      setAnimations({
        fadeInUp: {
          hidden: { opacity: 0, y: mobile ? 10 : 20 },
          visible: { 
            opacity: 1, 
            y: 0,
            transition: { 
              duration: mobile ? 0.4 : 0.6,
              ease: "easeOut"
            }
          }
        },
        fadeIn: {
          hidden: { opacity: 0 },
          visible: { 
            opacity: 1,
            transition: { 
              duration: mobile ? 0.3 : 0.5,
              ease: "easeOut"
            }
          }
        },
        staggerContainer: {
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: {
              staggerChildren: mobile ? 0.08 : 0.1,
              delayChildren: mobile ? 0.1 : 0.2,
              ease: "easeOut"
            }
          }
        },
        slideIn: {
          hidden: { x: mobile ? -10 : -20, opacity: 0 },
          visible: { 
            x: 0, 
            opacity: 1,
            transition: { 
              duration: mobile ? 0.3 : 0.5,
              ease: "easeOut"
            }
          }
        }
      });
    };

    // Initial setup
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return animations;
}

// Mobile touch optimization
export function useTouchOptimizer() {
  useEffect(() => {
    // Detect if device is touch-enabled
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    if (isTouchDevice) {
      // Add touch-specific class to body for CSS targeting
      document.body.classList.add('touch-device');
      
      // Optimize animations for touch devices by reducing motion
      // This helps with performance on lower-end mobile devices
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      
      if (prefersReducedMotion) {
        document.body.classList.add('reduced-motion');
      }
    }
    
    return () => {
      document.body.classList.remove('touch-device', 'reduced-motion');
    };
  }, []);
}

export default {
  fadeInUp,
  fadeIn,
  staggerContainer,
  slideIn,
  mobileMenuSlide,
  mobileMenuFade,
  useScrollInView,
  useDynamicAnimations,
  useTouchOptimizer
};